import { BioBlend, BioBlendComponent } from 'src/types';

export const toDocumentUuidArray = (documentUuid?: string | null): string[] | null => {
  return documentUuid ? [documentUuid] : null;
};

export const getPrimaryDocumentUuid = (
  documentUuid?: string | string[] | null,
  documents?: string[] | null
): string | null => {
  if (Array.isArray(documents)) {
    return documents[0] ?? null;
  }

  if (Array.isArray(documentUuid)) {
    return documentUuid[0] ?? null;
  }

  return documentUuid ?? null;
};

export const mergeDocumentGeneration = (
  primaryDocumentId?: string | null,
  primaryDocumentUuid?: string | null,
  fallbackDocumentId?: string | null,
  fallbackDocumentUuid?: string | null
): { documentId: string | null; documentUuid: string | null } => {
  const primary = {
    documentId: primaryDocumentId ?? null,
    documentUuid: primaryDocumentUuid ?? null,
  };
  const fallback = {
    documentId: fallbackDocumentId ?? null,
    documentUuid: fallbackDocumentUuid ?? null,
  };
  if (!primary.documentId && !primary.documentUuid) {
    return fallback;
  }

  const isSameGeneration =
    (!!primary.documentId &&
      !!fallback.documentId &&
      primary.documentId === fallback.documentId) ||
    (!!primary.documentUuid &&
      !!fallback.documentUuid &&
      primary.documentUuid === fallback.documentUuid);
  if (!isSameGeneration) {
    return primary;
  }

  return {
    documentId: primary.documentId ?? fallback.documentId,
    documentUuid: primary.documentUuid ?? fallback.documentUuid,
  };
};

export const mergeBlendDocumentMetadata = (primary: BioBlend, fallback?: BioBlend): BioBlend => {
  if (!fallback) {
    return primary;
  }

  const fallbackComponents = fallback.components || [];
  const bdnDocument = mergeDocumentGeneration(
    primary.bdnDocumentId,
    primary.bdnDocumentUuid,
    fallback.bdnDocumentId,
    fallback.bdnDocumentUuid
  );

  return {
    ...primary,
    bdnDocumentUuid: bdnDocument.documentUuid,
    bdnDocumentId: bdnDocument.documentId,
    bdnDocumentName: primary.bdnDocumentName ?? fallback.bdnDocumentName ?? null,
    bdnFieldSources: primary.bdnFieldSources ?? fallback.bdnFieldSources ?? {},
    components: (primary.components || []).map((component, index) =>
      mergeBlendComponentDocumentMetadata(component, fallbackComponents, index)
    ),
  };
};

export const mergeReusableBlendWithReportMetadata = (reusable: BioBlend, report: BioBlend): BioBlend => {
  const merged = mergeBlendDocumentMetadata(reusable, report);
  const reportComponents = report.components || [];
  return {
    ...merged,
    components: (merged.components || []).map((component, index) => {
      const reportComponent = findMatchingBlendComponent(component, reportComponents, index);
      if (!reportComponent) {
        return component;
      }
      const wasExplicitlyRemoved = !!reportComponent.posDocumentRemovalId;
      const reportDocumentUuid =
        reportComponent.posDocumentUuid ??
        getPrimaryDocumentUuid(reportComponent.documentUuid, reportComponent.documents);
      const hasActiveReportDocumentGeneration = !!(
        reportComponent.posDocumentId ||
        reportDocumentUuid ||
        reportComponent.posDocumentRemovalId
      );
      const reusableDocumentUuid =
        component.posDocumentUuid ?? getPrimaryDocumentUuid(component.documentUuid, component.documents);
      const tombstoneMatchesReusableDocument = !!(
        reportComponent.posDocumentRemovalUuid &&
        reportComponent.posDocumentRemovalUuid === reusableDocumentUuid
      );
      const hasReportDocumentGeneration =
        hasActiveReportDocumentGeneration || tombstoneMatchesReusableDocument;

      if (!hasReportDocumentGeneration) {
        return {
          ...component,
          posDocumentRemovalId: null,
        };
      }

      return {
        ...component,
        posDocumentId: reportComponent.posDocumentId ?? null,
        posDocumentUuid: wasExplicitlyRemoved ? null : reportDocumentUuid,
        posDocumentName: wasExplicitlyRemoved ? null : reportComponent.posDocumentName ?? null,
        posDocumentRemovalId: reportComponent.posDocumentRemovalId ?? null,
        posDocumentRemovalUuid: reportComponent.posDocumentRemovalUuid ?? null,
      };
    }),
  };
};

export const findMatchingBlendComponent = (
  primary: BioBlendComponent,
  candidates: BioBlendComponent[],
  index: number
): BioBlendComponent | undefined => {
  const byUuid = primary.uuid
    ? candidates.find((candidate) => candidate.uuid === primary.uuid)
    : undefined;
  if (byUuid) {
    return byUuid;
  }

  const byFields = candidates.find(
    (candidate) =>
      (!primary.uuid || !candidate.uuid) &&
      candidate.componentGrade === primary.componentGrade &&
      String(candidate.receivedMt) === String(primary.receivedMt)
  );
  if (byFields) {
    return byFields;
  }

  const byIndex = candidates[index];
  return !hasBlendComponentIdentity(primary) || !hasBlendComponentIdentity(byIndex)
    ? byIndex
    : undefined;
};

const hasBlendComponentIdentity = (component?: BioBlendComponent): boolean =>
  !!component?.uuid ||
  (!!component?.componentGrade &&
    component.receivedMt !== undefined &&
    component.receivedMt !== null &&
    String(component.receivedMt) !== 'undefined');

const mergeBlendComponentDocumentMetadata = (
  primary: BioBlendComponent,
  fallbackComponents: BioBlendComponent[],
  index: number
): BioBlendComponent => {
  const fallback = findMatchingBlendComponent(primary, fallbackComponents, index);

  if (!fallback) {
    return primary;
  }

  const fallbackPosDocumentUuid =
    fallback.posDocumentUuid ?? getPrimaryDocumentUuid(fallback.documentUuid, fallback.documents);
  if (
    primary.posDocumentRemovalId ||
    (!primary.posDocumentId &&
      !!primary.posDocumentRemovalUuid &&
      primary.posDocumentRemovalUuid === fallbackPosDocumentUuid)
  ) {
    return {
      ...primary,
      posDocumentId: null,
      posDocumentUuid: null,
      posDocumentName: null,
    };
  }

  const primaryPosDocumentUuid =
    primary.posDocumentUuid ?? getPrimaryDocumentUuid(primary.documentUuid, primary.documents);
  const posDocument = mergeDocumentGeneration(
    primary.posDocumentId,
    primaryPosDocumentUuid,
    fallback.posDocumentId,
    fallbackPosDocumentUuid
  );

  return {
    ...primary,
    posDocumentUuid: posDocument.documentUuid,
    posDocumentId: posDocument.documentId,
    posDocumentName: primary.posDocumentName ?? fallback.posDocumentName ?? null,
    posDocumentRemovalUuid:
      primary.posDocumentId || primary.posDocumentRemovalId ? primary.posDocumentRemovalUuid ?? null : null,
    posFieldSources: primary.posFieldSources ?? fallback.posFieldSources ?? {},
  };
};
